<template>
  <div v-if="embedded" class="col-12 refine-search-filter-embedded">
    <RefineSearchMultiCheckboxButton
      class="q-mb-sm"
      full-width-on-mobile
      label="Body type"
      :options="bodyStyleOptions"
      v-model="localSelectedBodyType"
      :disabled="disabled || isFetchRatebookLoading"
    />
    <div style="height: 18px"></div>
    <RefineSearchMultiCheckboxButton
      v-if="cabTypeOptions && cabTypeOptions.length > 0"
      class="q-mb-sm"
      full-width-on-mobile
      label="Ute type"
      :options="cabTypeOptions"
      v-model="localSelectedCabType"
      :disabled="!hasUteInBodyType || disabled || isFetchRatebookLoading"
    />
    <div style="height: 18px"></div>

    <RefineSearchGroupedCheckboxButton
      v-if="vehicleCategoryOptions && vehicleCategoryOptions.length > 0"
      class="q-mb-sm"
      full-width-on-mobile
      label="SUV type"
      :options="vehicleCategoryOptions"
      v-model="localSelectedVehicleCategory"
      :disabled="!hasSUVInBodyType || disabled || isFetchRatebookLoading"
    />

    <div style="height: 18px"></div>
    <RefineSearchMultiCheckboxButton
      v-if="seatOptions && seatOptions.length > 0"
      full-width-on-mobile
      label="Seats"
      :options="seatOptions"
      v-model="localSelectedSeat"
      :disabled="disabled || isFetchRatebookLoading"
    />
  </div>
  <CustomExpansionItem v-else class="col-12 q-mb-sm" label="Style" v-model="isOpen">
    <RefineSearchMultiCheckboxButton
      full-width-on-mobile
      label="Body type"
      :options="bodyStyleOptions"
      v-model="localSelectedBodyType"
      :disabled="disabled || isFetchRatebookLoading"
    />
    <RefineSearchMultiCheckboxButton
      v-if="cabTypeOptions && cabTypeOptions.length > 0"
      class="q-mb-sm"
      full-width-on-mobile
      label="Ute type"
      :options="cabTypeOptions"
      v-model="localSelectedCabType"
      :disabled="!hasUteInBodyType || disabled || isFetchRatebookLoading"
    />

    <RefineSearchGroupedCheckboxButton
      v-if="vehicleCategoryOptions && vehicleCategoryOptions.length > 0"
      class="q-mb-sm"
      full-width-on-mobile
      label="SUV type"
      :options="vehicleCategoryOptions"
      v-model="localSelectedVehicleCategory"
      :disabled="!hasSUVInBodyType || disabled || isFetchRatebookLoading"
    />
    <RefineSearchMultiCheckboxButton
      v-if="seatOptions && seatOptions.length > 0"
      full-width-on-mobile
      label="Seats"
      :options="seatOptions"
      v-model="localSelectedSeat"
      :disabled="disabled || isFetchRatebookLoading"
    />
  </CustomExpansionItem>
</template>

<script>
import CustomExpansionItem from '../CustomExpansionItem';
import RefineSearchMultiCheckboxButton from './RefineSearchMultiCheckboxButton';
import RefineSearchGroupedCheckboxButton from './RefineSearchGroupedCheckboxButton';

export default {
  name: 'StyleFilterSection',
  props: {
    selectedBodyType: {
      type: Object,
      default: () => ({})
    },
    selectedCabType: {
      type: Object,
      default: () => ({})
    },
    selectedVehicleCategory: {
      type: Object,
      default: () => ({})
    },
    selectedSeat: {
      type: Object,
      default: () => ({})
    },
    bodyStyleOptions: {
      type: Array,
      required: true
    },
    cabTypeOptions: {
      type: Array,
      required: true
    },
    vehicleCategoryOptions: {
      type: Array,
      required: true
    },
    seatOptions: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isFetchRatebookLoading: {
      type: Boolean,
      default: false
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      localSelectedBodyType: { ...this.selectedBodyType },
      localSelectedCabType: { ...this.selectedCabType },
      localSelectedVehicleCategory: JSON.parse(JSON.stringify(this.selectedVehicleCategory || {})),
      localSelectedSeat: { ...this.selectedSeat },
      // Separate flags for each watcher pair to prevent interference
      isSyncingBodyType: false,
      isSyncingCabType: false,
      isSyncingVehicleCategory: false,
      isSyncingSeat: false
    };
  },
  computed: {
    hasSUVInBodyType() {
      return this.filterResultsWithTrueValues(this.localSelectedBodyType)?.some((value) => {
        const formatValue = value.toLowerCase();
        return formatValue === 'suv';
      });
    },
    hasUteInBodyType() {
      return this.filterResultsWithTrueValues(this.localSelectedBodyType)?.some((value) => {
        const formatValue = value.toLowerCase();
        return formatValue == 'ute' || formatValue == 'utility';
      });
    }
  },
  watch: {
    selectedBodyType(newValue) {
      if (!this.isSyncingBodyType) {
        this.isSyncingBodyType = true;
        this.localSelectedBodyType = { ...newValue };
        this.$nextTick(() => {
          this.isSyncingBodyType = false;
        });
      }
    },
    selectedCabType(newValue) {
      if (!this.isSyncingCabType) {
        this.isSyncingCabType = true;
        this.localSelectedCabType = { ...newValue };
        this.$nextTick(() => {
          this.isSyncingCabType = false;
        });
      }
    },
    selectedVehicleCategory: {
      handler(newValue) {
        // Use separate flag to prevent interference from other watchers
        // Flag prevents circular updates: prop changes → update local → local watcher sees flag → doesn't emit → no loop
        if (!this.isSyncingVehicleCategory) {
          this.isSyncingVehicleCategory = true;
          this.localSelectedVehicleCategory = JSON.parse(JSON.stringify(newValue || {}));
          this.$nextTick(() => {
            this.isSyncingVehicleCategory = false;
          });
        }
      },
      deep: true,
      immediate: true
    },
    selectedSeat(newValue) {
      if (!this.isSyncingSeat) {
        this.isSyncingSeat = true;
        this.localSelectedSeat = { ...newValue };
        this.$nextTick(() => {
          this.isSyncingSeat = false;
        });
      }
    },
    localSelectedBodyType: {
      handler(newValue) {
        if (!this.isSyncingBodyType) {
          // Clear dependent selections if body type changes
          if (!this.hasUteInBodyType && Object.keys(this.localSelectedCabType).length > 0) {
            this.localSelectedCabType = {};
            this.$emit('update:selectedCabType', {});
          }
          if (!this.hasSUVInBodyType && Object.keys(this.localSelectedVehicleCategory).length > 0) {
            this.localSelectedVehicleCategory = {};
            this.$emit('update:selectedVehicleCategory', {});
          }
          this.$emit('update:selectedBodyType', newValue);
          this.$emit('change');
        }
      },
      deep: true
    },
    localSelectedCabType: {
      handler(newValue) {
        if (!this.isSyncingCabType) {
          this.$emit('update:selectedCabType', newValue);
          this.$emit('change');
        }
      },
      deep: true
    },
    localSelectedVehicleCategory: {
      handler(newValue) {
        // Use separate flag to prevent interference from other watchers
        // Flag prevents circular updates: user changes local → emit → prop changes → prop watcher sees flag → doesn't update local → no loop
        if (!this.isSyncingVehicleCategory) {
          this.$emit('update:selectedVehicleCategory', newValue);
          this.$emit('change');
        }
      },
      deep: true
    },
    localSelectedSeat: {
      handler(newValue) {
        if (!this.isSyncingSeat) {
          this.$emit('update:selectedSeat', newValue);
          this.$emit('change');
        }
      },
      deep: true
    }
  },
  methods: {
    filterResultsWithTrueValues(objValue) {
      if (objValue) {
        return Object.keys(objValue).filter((value) => objValue[value]);
      }
      return objValue;
    }
  },
  components: {
    CustomExpansionItem,
    RefineSearchMultiCheckboxButton,
    RefineSearchGroupedCheckboxButton
  }
};
</script>
